import React from "react";
import {
  REQUEST_TYPE_ORDER,
  REQUEST_TYPES,
  type RequestTypeId,
} from "./requestTypes";

interface RequestTypeCardsProps {
  onSelect: (type: RequestTypeId) => void;
}

/**
 * The entry point to open a ticket: one card per request type, each saying
 * what it is for, so a customer who does not know the categories can still
 * pick the right one. Two per row on narrow screens, four on wide ones.
 * Buttons with the description in a tooltip were tried and dropped: Eduardo
 * preferred the description in sight (CTX-1256).
 *
 * On a phone the four cards with their descriptions took half the screen and
 * left the ticket list a sliver, so below `sm` they shrink to icon and label;
 * the description stays for screen readers (CTX-1339).
 */
export function RequestTypeCards({ onSelect }: RequestTypeCardsProps) {
  return (
    <div className="grid grid-cols-1 xs375:grid-cols-2 lg:grid-cols-4 gap-3">
      {REQUEST_TYPE_ORDER.map((id) => {
        const { label, description, Icon } = REQUEST_TYPES[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            className="
              group flex items-center gap-2.5 rounded-md border border-semantic-border-primary
              bg-semantic-bg-primary p-2.5 text-left transition-colors sm:items-start sm:gap-3 sm:p-3
              hover:border-semantic-border-brand-primary hover:bg-semantic-bg-brand-secondary
              focus:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-brand-primary
            "
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-semantic-bg-brand-secondary text-semantic-text-brand sm:h-9 sm:w-9">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-tight text-semantic-text-primary sm:leading-normal">
                {label}
              </span>
              <span className="sr-only mt-0.5 block text-xs text-semantic-text-secondary sm:not-sr-only">
                {description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default RequestTypeCards;
